"use client";

import React from "react";
import {
  OakBox,
  OakGrid,
  OakGridArea,
  OakHeading,
  OakLI,
  OakListItem,
  OakOL,
  OakP,
  OakSecondaryLink,
  OakSpan,
  OakUL,
} from "@oaknational/oak-components";

import type { LessonListItem as LessonItem } from "@/types/ui";

type Props = {
  index: number;
  lesson: LessonItem;
  getPupilLessonUrl: (
    programmeSlug: string,
    unitSlug: string,
    lessonSlug: string,
  ) => string;
  onLessonPreviewed?: (lessonSlug: string) => void;
};
export const LessonListItem = ({
  index,
  lesson,
  getPupilLessonUrl,
  onLessonPreviewed,
}: Props) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const expandedContent = (
    <OakGrid $cg={"spacing-48"} $rg={"spacing-40"}>
      <OakGridArea $colSpan={[12, 6, 6]}>
        <OakHeading tag={"h2"} $font={"heading-7"} $mb={"spacing-12"}>
          Key learning points
        </OakHeading>
        <OakOL $font={"body-3"}>
          {lesson.lessonData?.keyLearningPoints?.map(
            ({ keyLearningPoint }, i) => (
              <OakLI
                key={keyLearningPoint + i}
                $mb={"spacing-12"}
                $font={"body-3"}
              >
                {keyLearningPoint}
              </OakLI>
            ),
          )}
        </OakOL>
      </OakGridArea>
      <OakGridArea $colSpan={[12, 6, 6]}>
        <OakHeading tag={"h2"} $font={"heading-7"} $mb={"spacing-12"}>
          Keywords
        </OakHeading>
        <OakUL $pa={"spacing-0"}>
          {lesson.lessonData?.keywords?.map(({ description, keyword }) => (
            <OakLI key={keyword} $mb={"spacing-12"}>
              <OakP $font={"body-3"}>
                <OakSpan $font={"body-3-bold"}>{keyword}</OakSpan> -{" "}
                {description}
              </OakP>
            </OakLI>
          ))}
        </OakUL>
      </OakGridArea>
      <OakGridArea $colSpan={[12, 6, 6]}>
        <OakHeading tag={"h2"} $font={"heading-7"} $mb={"spacing-12"}>
          Common misconception
        </OakHeading>
        {lesson.lessonData?.misconceptionsAndCommonMistakes
          ?.filter(
            ({ misconception, response }) => !!misconception || !!response,
          )
          ?.map(({ misconception, response }) => (
            <OakBox key={misconception} $mb={"spacing-12"}>
              <OakP $font={"body-3-bold"}>{misconception}</OakP>
              <OakP $font={"body-3"}>{response}</OakP>
            </OakBox>
          ))}
      </OakGridArea>
      {lesson.lessonData?.equipmentAndResources &&
        lesson.lessonData.equipmentAndResources.length > 0 && (
          <OakGridArea $colSpan={[12, 6, 6]}>
            <OakHeading tag={"h2"} $font={"heading-7"} $mb={"spacing-12"}>
              Equipment
            </OakHeading>
            <OakUL $pa={"spacing-0"}>
              {lesson.lessonData.equipmentAndResources.map(({ equipment }) => (
                <OakLI key={equipment} $mb={"spacing-12"}>
                  <OakP $font={"body-3"}>{equipment}</OakP>
                </OakLI>
              ))}
            </OakUL>
          </OakGridArea>
        )}
    </OakGrid>
  );
  return (
    <OakListItem
      asRadio
      key={index}
      radioValue={lesson.lessonSlug}
      index={index}
      title={lesson.lessonData.title}
      isLegacy={lesson.isLegacy}
      isExpanded={isExpanded}
      expandedContent={expandedContent}
      middleSlot={
        <OakSecondaryLink
          iconName={"external"}
          isTrailingIcon={true}
          target={"_blank"}
          href={getPupilLessonUrl(
            lesson.programmeSlug,
            lesson.unitSlug,
            lesson.lessonSlug,
          )}
          onClick={() => {
            onLessonPreviewed?.(lesson.lessonSlug);
          }}
        >
          Preview Lesson
        </OakSecondaryLink>
      }
      endSlot={
        <OakSecondaryLink
          element={"button"}
          type="button"
          iconName={isExpanded ? "chevron-up" : "chevron-down"}
          isTrailingIcon={true}
          onClick={() => {
            setIsExpanded((prev) => !prev);
          }}
        >
          Lesson Details
        </OakSecondaryLink>
      }
    />
  );
};
